import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    sisterPoints:          Array,
    sisterEquation:        String,
    vesselEquationFactors: Array,
    vesselPoints:          Array,
    showTitle:             Boolean,
    hideSistersScatter:    Boolean,
    hideSistersTrend:      Boolean,
    hideVesselLine:        Boolean,
    hideVesselPoints:      Boolean,
  }

  connect() {
    google.charts.setOnLoadCallback(() => {
      if (
        google.visualization?.DataTable !== undefined &&
        google.visualization?.ScatterChart !== undefined
      ) {
        this.drawChart();
      }
    });
  }

  drawChart() {
    const el = this.element;
    const isLaden = el.classList.contains('laden');
    const color = isLaden ? '#006E73' : '#96181a';

    const {
      sisterPointsValue:          sisterPoints,
      sisterEquationValue:        sisterEquation,
      vesselEquationFactorsValue: eqFactors,
      vesselPointsValue:          vesselPoints,
      showTitleValue:             showTitle,
      hideSistersScatterValue:    hideSistersScatter,
      hideSistersTrendValue:      hideSistersTrend,
      hideVesselLineValue:        hideVesselLine,
      hideVesselPointsValue:      hideVesselPoints,
    } = this;

    // --- Parse sister equation ---
    const regex = /^y.=.(.*)e\^\((.*)x\)$/;
    const factors = sisterEquation ? regex.exec(sisterEquation) : null;

    // --- Build DataTable ---
    let table = new google.visualization.arrayToDataTable([
      [{ label: 'X', type: 'number' }, { label: 'Sistership', type: 'number' }],
      ...sisterPoints
    ]);

    const vesselTable = new google.visualization.arrayToDataTable(
      vesselPoints.length ? [['X', 'Vessel'], ...vesselPoints] : [['X', 'Vessel']]
    );

    // Fill in missing X values for smooth curves
    const existingX = table.getDistinctValues(0);
    [10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15].forEach(x => {
      if (!existingX.includes(x)) table.addRow([x, null]);
    });

    table = new google.visualization.data.join(table, vesselTable, 'full', [[0, 0]], [1], [1]);

    // --- Columns at fixed indices ---
    // 0: X
    // 1: Sisters scatter  (series 0)
    // 2: Vessel points    (series 1)
    // 3: Vessel curve     (series 2)
    // 4: Sisters trend    (series 3)
    const view = new google.visualization.DataView(table);
    view.setColumns([
      0,
      1,
      2,
      {
        label: 'Vessel line',
        type: 'number',
        calc: (dt, row) => {
          if (!eqFactors?.length || hideVesselLine) return null;
          const x = dt.getValue(row, 0);
          return eqFactors[0] * Math.pow(x, eqFactors[1]);
        }
      },
      {
        label: 'Trend',
        type: 'number',
        calc: (dt, row) => {
          if (!factors || hideSistersTrend) return null;
          const x = dt.getValue(row, 0);
          return factors[1] * Math.exp(factors[2] * x);
        }
      }
    ]);

    // --- Axis ranges ---
    const xRange = table.getColumnRange(0);
    const minY = Math.min(table.getColumnRange(1).min, table.getColumnRange(2).min);
    const maxY = Math.max(table.getColumnRange(1).max, table.getColumnRange(2).max);

    // --- Chart options ---
    const hidden = (extra = {}) => ({
      opacity: 0, visibleInLegend: false, enableInteractivity: false, pointSize: 0, ...extra
    });
    const visible = (extra = {}) => ({
      opacity: 1, visibleInLegend: false, ...extra
    });

    const options = {
      width: window.innerWidth < 576 ? '100%' : 750,
      height: 400,
      chartArea: { width: '80%', height: '75%' },
      seriesType: 'scatter',
      series: {
        0: hideSistersScatter ? hidden()        : visible({ type: 'scatter', color: '#bdbdbd' }),
        1: hideVesselPoints   ? hidden()        : visible({ type: 'scatter', pointSize: 12, color }),
        2: hideVesselLine     ? hidden()        : visible({ type: 'line', color, lineDashStyle: [4, 1] }),
        3: hideSistersTrend   ? hidden()        : visible({ type: 'line', color: '#bdbdbd', opacity: 0.4 }),
      },
      hAxis: {
        title: 'Speed [kn]',
        viewWindow: { min: xRange.min - 1, max: xRange.max + 1 },
        gridlines: { count: -1 }
      },
      vAxis: {
        title: 'Consumption [MT/day]',
        viewWindow: { min: minY - 5, max: maxY + 3 },
        gridlines: { count: -1 }
      },
      legend: { position: 'bottom' },
      ...(showTitle && { title: isLaden ? 'Sister analysis - Laden' : 'Sister analysis - Ballast' })
    };

    el.innerHTML = '';
    new google.visualization.ComboChart(el).draw(view, options);
  }
}